import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  ErrorState,
  NoPermissionState,
  PageHeader,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr,
  UserCell,
} from '@stayafrica/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { RotateCcw } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/features/auth/session';
import { creditNotesQueryOptions } from '@/features/billing/queries';
import { creditNoteTone } from '@/features/billing/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort } from '@/lib/format';

export const Route = createFileRoute('/_app/billing/credit-notes/')({
  component: CreditNotesPage,
});

function CreditNotesPage() {
  const { t, i18n } = useTranslation(['billing', 'common', 'errors']);
  const canRead = useCan('invoice.read');
  const query = useInfiniteQuery(creditNotesQueryOptions({}, 25, canRead));
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('billing:creditNotes.title')}
        subtitle={t('billing:creditNotes.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('billing:invoices.title'),
            link: <Link to="/billing/invoices">{t('billing:invoices.title')}</Link>,
          },
          { label: t('billing:creditNotes.title') },
        ]}
      />

      <Card>
        <CardHeader
          title={t('billing:creditNotes.listTitle')}
          icon={<RotateCcw className="ico ico-16" aria-hidden="true" />}
          iconTone="red"
          actions={
            <Badge tone="neutral">
              {t('billing:creditNotes.countLabel', { count: rows.length })}
            </Badge>
          }
        />
        <CardBody density="flush">
          {query.isPending ? (
            <TableSkeleton rows={4} columns={7} label={t('common:states.loading')} />
          ) : query.isError ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(query.error, t)}
              traceId={errorTraceId(query.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void query.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<RotateCcw className="ico ico-24" aria-hidden="true" />}
              title={t('billing:creditNotes.emptyTitle')}
              description={t('billing:creditNotes.emptyBody')}
            />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('billing:creditNotes.columns.number')}</Th>
                    <Th>{t('billing:creditNotes.columns.invoice')}</Th>
                    <Th>{t('billing:creditNotes.columns.billingParty')}</Th>
                    <Th>{t('billing:creditNotes.columns.issueDate')}</Th>
                    <Th align="right">{t('billing:creditNotes.columns.amount')}</Th>
                    <Th>{t('billing:creditNotes.columns.reason')}</Th>
                    <Th>{t('billing:creditNotes.columns.status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <Tr key={row.id}>
                      <Td>
                        <Link
                          to="/billing/credit-notes/$creditNoteId"
                          params={{ creditNoteId: row.id }}
                        >
                          <span className="cell-id">
                            {row.number ?? t('billing:status.creditNote.DRAFT')}
                          </span>
                        </Link>
                      </Td>
                      <Td>
                        <Link
                          to="/billing/invoices/$invoiceId"
                          params={{ invoiceId: row.invoiceId }}
                        >
                          <span className="cell-id">{row.invoiceNumber ?? '—'}</span>
                        </Link>
                      </Td>
                      <Td>
                        <UserCell
                          avatar={<Avatar name={row.billingParty.displayName} size="sm" />}
                          name={row.billingParty.displayName}
                        />
                      </Td>
                      <Td className="t-13" numeric>
                        {row.issueDate ? formatDateShort(row.issueDate, i18n.language) : '—'}
                      </Td>
                      <Td align="right" numeric>
                        −{formatAmount(row.totals.netTotal, i18n.language)}
                      </Td>
                      <Td className="t-13 truncate" style={{ maxWidth: 280 }}>
                        {row.reason}
                      </Td>
                      <Td>
                        <Badge tone={creditNoteTone(row.status)} dot>
                          {t(`billing:status.creditNote.${row.status}`)}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </CardBody>

        {query.hasNextPage ? (
          <div className="card__foot">
            <Button
              variant="secondary"
              size="sm"
              loading={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {t('common:pagination.loadMore')}
            </Button>
          </div>
        ) : null}
      </Card>
    </Container>
  );
}
